import { isRunningInSEBBrowser } from '@amplify/secure-exam-browser';
import { isKioskChromeOS } from '../common/utils/browserUtils';
import { HTTP_RESPONSE_CODE } from './http';

export const MIC_PERMISSION_ERROR_NAME = 'Permission denied';

export const ERROR_STATUS = {
    HTTP_ERROR: 'HTTP_ERROR',
    NETWORK_ERROR: 'NETWORK_ERROR',
    UNEXPECTED_RESPONSE: 'UNEXPECTED_RESPONSE',
    NO_FORM_CONTENT: 'NO_FORM_CONTENT',
    AUDIO_SIZE_THRESHOLD_NOT_MET: 'AUDIO_SIZE_THRESHOLD_NOT_MET',
};

export const LANTERN_ERROR_CODES = {
    NO_CONNECTION: 1000,
    CONNECTION_TIMEOUT: 1001,
    NO_FORM_CONTENT: 1002,
    UNKNOWN_ERROR: 1003,
    AUDIO_SIZE_THRESHOLD_NOT_MET: 1004,
    OFFLINE_RESULT: 1005,
    CHROME_OS_WITHOUT_KIOSK: 1006,
};

export const NETWORK_ERRORS = [
    HTTP_RESPONSE_CODE.GATEWAY_TIMEOUT,
    HTTP_RESPONSE_CODE.REQUEST_TIMEOUT,
    LANTERN_ERROR_CODES.CONNECTION_TIMEOUT,
];

export const RESULTS_ERRORS = [
    HTTP_RESPONSE_CODE.CONFLICT,
    HTTP_RESPONSE_CODE.BAD_RESPONSE,
];

export const RETRIABLE_ERRORS = [
    ...NETWORK_ERRORS,
    HTTP_RESPONSE_CODE.SERVER_ERROR,
    LANTERN_ERROR_CODES.NO_CONNECTION,
];

export const AR_TRO_RETRIABLE_ERRORS = Object.values(HTTP_RESPONSE_CODE).concat(404);

export const VR_RESULT_STATUS = {
    UNAVAILABLE: "unavailable",
    PENDING: "pending",
    READY: "ready",
    FAILED: "failed",
};

export const BROWSER_ERROR_MESSAGES = {
    FAILED_TO_FETCH: 'failed to fetch', // Chrome
    APPEARS_OFFLINE: 'connection appears to be offline', // Safari
    CANCELLED: 'cancelled', // Safari when VPN drops
    TIMED_OUT: 'the request timed out', // Safari on fly mode
    LOAD_FAILED: 'load failed', // Safari when internet drops
};

export const VR_ERROR_REASONS = {
    UNKNOWN: "unknown",
    TIMEOUT: "timeout",
    UNTRANSCRIBABLE: "untranscribable",
};

export const getUnexpectedErrorMessage = (errorNum) => `Educators: An unexpected error ${errorNum} occurred. Please make a note of the error number and report it to us.`;

export const NO_INTERNET_MESSAGE = 'Educators: Please check your internet connection and try again.';

export const SERVER_ERROR_MEESAGE = 'Educators: A server error occurred. Please try again later.';

export const NETWORK_ERROR_MESSAGE = 'Educators: There\'s a network issue. Please try again later.';

export const UNAUTHORIZED_ERROR_MESSAGE = 'Educators: Session has timed out. Please log in again.';

export const PAGE_REFRESH_MESSAGE = 'Educators: The page did not load correctly. Click the "Refresh" button for your browser to reload the page.';

export const MISSING_FORM_MESSAGE = 'Educators: No assessment available for this period.';

export const DEFAULT_NO_CONNECTION_MESSAGE = 'Educators: Please check your internet connection and try again.';

export const VR_RESULT_PROCESSING_MESSAGE = 'We are working on it. This may take a few minutes.';

export const INVALIDATE_RESULT_MESSAGE = 'Educators: Please invalidate the result and restart the assessment.';

export const INVALIDATE_RESULT_MESSAGE_WITH_REMINDER = `${INVALIDATE_RESULT_MESSAGE} Remind the student to read out loud next time.`;

export const SOMETHING_WENT_WRONG_MESSAGE = 'Something went wrong.';

export const AUDIO_SIZE_ERROR_MESSAGE =
    'Educators: There was a problem with the audio. If the student is using bluetooth headphones, disconnect the headphones from bluetooth, turn off bluetooth on the device, and then start over using wired headphones.';

export const UNABLE_TO_DETECT_READING_MESSAGE = 'Sorry, unable to detect reading earlier.';

export const VR_RESULT_PROCESSING_TIMOUT_MESSAGE = 'Sorry, this is taking longer than expected. Take a break and try again later.';

export const RESUME_PROCESSING_MESSAGE = 'Sorry, we are still processing your TRO results. Take a break and try again in a few minutes.';

export const VR_RESULT_PROCESSING_TITLE = 'Please wait.';

export const VR_RESULT_PROCESSING_TIMEOUT_TITLE = 'Try again later.';

export const DEFAULT_HELP_POPUP_TITLE = 'Raise your hand for help.';

export const AUDIO_SAVING_MESSAGE = 'Audio saving in progress. This may take a few minutes.';

export const RESULT_NOT_FOUND_ERROR = 'Result not found';

export const OFFLINE_RESULT_MESSAGE = 'Your device is not connected to the internet. The assessment result is saved and will be available next time you log in with internet connection.';

// TODO: should be changed after messaging created
export const OFFLINE_RESULT_SEB_MESSAGE = 'Your device is not connected to the internet. The assessment result is only saved while the secure browser is open. Do not close the browser until you are connected to the internet.';

export const OFFLINE_RESULT_KIOSK_MESSAGE = 'Your device is not connected to the internet. The assessment result is only saved while kiosk mode is open. Do not close the browser until you are connected to the internet.';

export const CHROME_OS_WITHOUT_KIOSK_MESSAGE = 'For secure EOY assessment, you must open this app in kiosk mode. Click the Apps icon and select mCLASS Student Assessment.';

export const getUserErrorMessage = (status) => {
    if (NETWORK_ERRORS.includes(status)) {
        return NETWORK_ERROR_MESSAGE;
    }
    switch (status) {
    case HTTP_RESPONSE_CODE.SERVER_ERROR:
        return SERVER_ERROR_MEESAGE;
    case HTTP_RESPONSE_CODE.UNAUTHORIZED:
        return UNAUTHORIZED_ERROR_MESSAGE;
    case LANTERN_ERROR_CODES.NO_CONNECTION:
        return NO_INTERNET_MESSAGE;
    case LANTERN_ERROR_CODES.NO_FORM_CONTENT:
        return MISSING_FORM_MESSAGE;
    case LANTERN_ERROR_CODES.OFFLINE_RESULT:
        if (isKioskChromeOS()) {
            return OFFLINE_RESULT_KIOSK_MESSAGE;
        }
        if (isRunningInSEBBrowser()) {
            return OFFLINE_RESULT_SEB_MESSAGE;
        }
        return OFFLINE_RESULT_MESSAGE;
    case LANTERN_ERROR_CODES.CHROME_OS_WITHOUT_KIOSK:
        return CHROME_OS_WITHOUT_KIOSK_MESSAGE;
    default:
        return getUnexpectedErrorMessage(status);
    }
};

export const getShouldInvalidateResult = (statuses, reasons) =>
    reasons.includes(VR_ERROR_REASONS.UNTRANSCRIBABLE) || reasons.includes(VR_ERROR_REASONS.UNKNOWN) || statuses.includes(VR_RESULT_STATUS.UNAVAILABLE);
